import { useState } from 'react';
import CreateBudget from './createAndEdit';
import { IBudgetFormData } from './createAndEdit/types';
import { createEmptyBudgetFormData } from './createAndEdit/utils/mockData';
import DetailBudget from './detail';
import { useBudgetActions } from './hooks/useBudgetActions';
import { useBudgetView } from './hooks/useBudgetView';
import { useBudgetsList } from './hooks/useBudgetsList';
import BudgetsList from './list';
import { Budget, IBudgetsFormProps } from './types';

const BudgetsForm = (props: IBudgetsFormProps) => {
  const [budgetFormData, setBudgetFormData] = useState<IBudgetFormData | null>(null);
  const { view, currentBudgetId, goToList, goToCreate, goToEdit, goToDetail } = useBudgetView();
  const { budgetsListResponse, userProfile, loadBudgets } = useBudgetsList({
    occurrenceId: props.occurrenceId,
    view,
  });

  const handleNavigateToCreate = () => {
    const budgetId = `budget-${Date.now()}`;
    const emptyData = createEmptyBudgetFormData(props.occurrenceId, budgetId);
    setBudgetFormData(emptyData);
    goToCreate(budgetId);
  };

  const handleNavigateToView = (budgetId: string, data: IBudgetFormData) => {
    setBudgetFormData(data);
    goToDetail(budgetId);
  };

  const handleNavigateToEdit = (budgetId: string, data: IBudgetFormData) => {
    setBudgetFormData(data);
    goToEdit(budgetId);
  };

  const handleSuccess = () => {
    setBudgetFormData(null);
    goToList();
  };

  const { handleAcceptBudget, handleRejectBudget } = useBudgetActions({
    currentBudgetId,
    budgetsListResponse,
    budgetFormData,
    userProfile,
    onSuccess: handleSuccess,
  });

  const handleCloseDetail = () => {
    setBudgetFormData(null);
    goToList();
  };

  const handleSaveBudget = () => {
    setBudgetFormData(null);
    goToList();
  };

  if (view === 'create' || view === 'edit') {
    if (!budgetFormData) {
      if (view === 'create') {
        const budgetId = currentBudgetId || `budget-${Date.now()}`;
        const initialData = createEmptyBudgetFormData(props.occurrenceId, budgetId);
        setBudgetFormData(initialData);
      }
      // Para edit, os dados serão carregados pelo handleEditBudget
      return null;
    }

    return (
      <CreateBudget
        mode={view === 'create' ? 'create' : 'edit'}
        occurrenceId={props.occurrenceId}
        budgetId={budgetFormData.budgetId}
        initialData={budgetFormData}
        onSave={handleSaveBudget}
        onClose={handleCloseDetail}
      />
    );
  }

  if (view === 'detail') {
    if (!budgetFormData || !currentBudgetId) {
      return null;
    }

    const budgetItem = budgetsListResponse?.budgets.find((item) => item.uuid === currentBudgetId);

    return (
      <DetailBudget
        occurrenceId={props.occurrenceId}
        budgetId={currentBudgetId}
        initialData={budgetFormData}
        onClose={handleCloseDetail}
        onAccept={handleAcceptBudget}
        onReject={handleRejectBudget}
        budgetStatus={budgetItem?.status}
        budgetDate={budgetItem?.updatedAt || budgetItem?.createdAt}
        onViewAttachments={props.onViewAttachments}
      />
    );
  }

  const budgets: Budget[] =
    budgetsListResponse?.budgets.map((item) => ({
      id: item.uuid,
      consultantName: item.consultant,
      origin: item.origin,
      originUserName: item.originUserName,
      recipient: item.recipient,
      user: item.username,
      version: item.lastVersionNumber,
      date: new Date(item.createdAt).toLocaleDateString('pt-BR'),
      status: item.status,
      hasHistory: true,
      lastVersionNumber: item.lastVersionNumber,
    })) || [];

  return (
    <BudgetsList
      currentStep={props.currentStep}
      budgets={budgets}
      occurrenceId={props.occurrenceId}
      budgetsListResponse={budgetsListResponse}
      userProfile={userProfile}
      onNavigateToCreate={handleNavigateToCreate}
      onNavigateToView={handleNavigateToView}
      onNavigateToEdit={handleNavigateToEdit}
      onLoadBudgets={loadBudgets}
    />
  );
};

export default BudgetsForm;
